import React, { useState, useEffect, useRef } from 'react';
import { Eye, ChevronDown } from 'lucide-react';
import { Modal } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { fetchManagerChatLogs, fetchManagerChatLogById, ChatLogListItem, ChatLogMessageItem } from '../../services/api';
import { useChatSession } from '../../contexts/ChatSessionContext';
import Preloder from '../../components/Preloder';
import { logger } from '../../utils/logger';
import { formatAIText } from 'src/utils/formatAIText';
import { formatSopReferences, resolveAssistantSourceLine } from '../../utils/chatLogSource';

// Use the chat log types from admin reports
type ManagerChatLog = ChatLogListItem & {
    messages?: ChatLogMessageItem[];
};

type UserType = 'all' | 'manager' | 'worker';

type PageButton = { key: string; page?: number; label: string | number; active?: boolean; disabled?: boolean };
function getPageNumbers(current: number, total: number): PageButton[] {
    const buttons: PageButton[] = [];
    const pushPage = (p: number, active = false) => buttons.push({ key: `p-${p}`, page: p, label: p, active });
    const pushEllipsis = (id: string) => buttons.push({ key: `e-${id}`, label: '…', disabled: true });
    if (total <= 5) {
        for (let i = 1; i <= total; i++) pushPage(i, i === current);
        return buttons;
    }
    pushPage(1, current === 1);
    if (current > 3) pushEllipsis('l');
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    for (let i = start; i <= end; i++) pushPage(i, i === current);
    if (current < total - 2) pushEllipsis('r');
    pushPage(total, current === total);
    return buttons;
}

const PastChats: React.FC = () => {
    const { refreshTrigger } = useChatSession();
    const [sessions, setSessions] = useState<ManagerChatLog[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [initialLoading, setInitialLoading] = useState<boolean>(true);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [flaggedOnly, setFlaggedOnly] = useState<boolean>(false);
    const [userType, setUserType] = useState<UserType>('all');
    const [isUserTypeDropdownOpen, setIsUserTypeDropdownOpen] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [rowsPerPage, setRowsPerPage] = useState<number>(9);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [viewChatModalOpen, setViewChatModalOpen] = useState<boolean>(false);
    const [selectedChat, setSelectedChat] = useState<ManagerChatLog | null>(null);
    const userTypeDropdownRef = useRef<HTMLDivElement>(null);

    // Server-side pagination and search are handled by the API
    const formatDate = React.useCallback((dateString: string) => {
        // Ensure timestamp is treated as UTC
        const normalizedTimestamp = dateString.endsWith('Z') ? dateString : dateString + 'Z';
        const date = new Date(normalizedTimestamp);

        // Format date only
        const dateOnly = date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });

        // Format time only
        const timeOnly = date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
        });

        return `${dateOnly} | ${timeOnly}`;
    }, []);

    // Apply client-side date filter (API currently doesn't support date param)
    const isDateFiltered = !!selectedDate;
    const paginatedSessions = isDateFiltered
        ? sessions.filter((s) => {
            const sessionDate = new Date(s.date);
            const filterDate = selectedDate!;
            return sessionDate.toDateString() === filterDate.toDateString();
        })
        : sessions;

    // Apply flagged filter
    const filteredSessions = flaggedOnly
        ? paginatedSessions.filter((s) => s.warnings > 0)
        : paginatedSessions;

    // Calculate displayed total based on all client-side filters
    const displayedTotal = isDateFiltered || flaggedOnly ? filteredSessions.length : totalCount;

    // For client-side filtering, implement pagination on filtered results
    const isClientSideFiltered = isDateFiltered || flaggedOnly;
    const startIndex = isClientSideFiltered ? (currentPage - 1) * rowsPerPage : 0;
    const endIndex = isClientSideFiltered ? startIndex + rowsPerPage : filteredSessions.length;
    const paginatedFilteredSessions = isClientSideFiltered
        ? filteredSessions.slice(startIndex, endIndex)
        : filteredSessions;


    useEffect(() => {
        const loadSessions = async () => {
            try {
                // Show loading for all operations except initial load (which shows Preloder)
                if (!initialLoading) {
                    setLoading(true);
                }

                // When flaggedOnly is active, load all records to enable client-side filtering
                // Otherwise, use server-side pagination
                const shouldLoadAll = flaggedOnly;
                const pageSize = shouldLoadAll ? 1000 : rowsPerPage; // Load large number when filtering
                const page = shouldLoadAll ? 1 : currentPage;

                // Load manager chat logs from the chat_logs collection (same as admin reports)
                const response = await fetchManagerChatLogs({
                    page: page,
                    pageSize: pageSize,
                    search: searchQuery,
                    role: userType === 'all' ? undefined : userType
                });
                const sessions = response.items || [];
                const total = response.total || 0;

                setSessions(sessions);
                setTotalCount(total);
            } catch (error) {
                logger.error('Failed to load manager chat logs:', error);
                setSessions([]);
            } finally {
                setLoading(false);
                setInitialLoading(false);
            }
        };

        loadSessions();
    }, [refreshTrigger, currentPage, searchQuery, rowsPerPage, userType, initialLoading, flaggedOnly]);

    // Removed auto-refresh on tab change to prevent unnecessary API calls
    // Users can manually refresh if needed, or the page will refresh when filters change

    // Reset to first page when filters change for consistent UX
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, selectedDate, flaggedOnly, userType]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (userTypeDropdownRef.current && !userTypeDropdownRef.current.contains(event.target as Node)) {
                setIsUserTypeDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);



    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const clearDateFilter = () => {
        setSelectedDate(null);
    };

    return (
        <>
            {initialLoading && <Preloder />}
            <div className="space-y-6 min-h-0">
                {/* Controls */}
                <div className="px-0">
                    <div className="flex flex-col md:flex-row gap-3 md:gap-4 items-stretch md:items-center md:justify-between pt-1 md:pt-2">
                        <div className="flex items-center gap-3">
                            <div className="w-full md:w-80 shrink-0 pl-2 md:pl-3">
                                <div className="relative">
                                    <span className="absolute inset-y-0 left-3 flex items-center text-gray-400">
                                        <img src="/icons/searchIcon.png" alt="Search" className="w-4 h-4" />
                                    </span>
                                    <input
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder="Search Chats.."
                                        className="w-full h-10 pl-9 pr-4 py-1 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                                    />
                                </div>
                            </div>
                            <div className="relative custom-datepicker-wrapper">
                                <DatePicker
                                    selected={selectedDate}
                                    onChange={(date: Date | null) => setSelectedDate(date)}
                                    dateFormat="yyyy-MM-dd"
                                    placeholderText="Select Date"
                                    className="h-10 pl-10 pr-6 py-1 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 text-sm bg-white w-full"
                                    showPopperArrow={false}
                                />
                                <span className="absolute inset-y-0 left-3 flex items-center text-gray-400 pointer-events-none">
                                    <img src="/icons/calendar.png" alt="Calendar" className="w-4 h-4" />
                                </span>
                                {selectedDate && (
                                    <button
                                        onClick={clearDateFilter}
                                        className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600"
                                        title="Clear date filter"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                )}
                            </div>
                            <div className="relative" ref={userTypeDropdownRef}>
                                <button
                                    onClick={() => setIsUserTypeDropdownOpen(!isUserTypeDropdownOpen)}
                                    className="w-48 h-10 px-3 py-2 flex items-center justify-between bg-white border border-gray-300 rounded-xl text-sm text-gray-700 transition-colors shadow-sm hover:bg-gray-50"
                                >
                                    <span className={`${userType === 'all' ? 'text-gray-500' : 'text-gray-700'} font-normal`}>
                                        {userType === 'all' ? 'Select User Type' : userType === 'manager' ? 'My Chats' : 'Workers'}
                                    </span>
                                    <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isUserTypeDropdownOpen ? 'rotate-180' : ''}`} />
                                </button>
                                {isUserTypeDropdownOpen && (
                                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-xl shadow-lg z-10">
                                        <div className="py-1">
                                            <button
                                                onClick={() => { setUserType('all'); setIsUserTypeDropdownOpen(false); setCurrentPage(1); }}
                                                className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 rounded-lg font-normal"
                                            >
                                                Select User Type
                                            </button>
                                            <button
                                                onClick={() => { setUserType('manager'); setIsUserTypeDropdownOpen(false); setCurrentPage(1); }}
                                                className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 rounded-lg font-normal"
                                            >
                                                My Chats
                                            </button>
                                            <button
                                                onClick={() => { setUserType('worker'); setIsUserTypeDropdownOpen(false); setCurrentPage(1); }}
                                                className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 rounded-lg font-normal"
                                            >
                                                Workers
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-14-bold grey-800">Flagged Only</span>
                                <button
                                    onClick={() => setFlaggedOnly(!flaggedOnly)}
                                    className={`relative inline-flex h-8 w-20 items-center rounded-full transition-colors focus:outline-none ${flaggedOnly ? 'bg-red-500' : 'bg-gray-200'
                                        }`}
                                >
                                    <span
                                        className={`inline-block h-6 w-10 transform rounded-full bg-white transition-transform ${flaggedOnly ? 'translate-x-9' : 'translate-x-1'
                                            }`}
                                    />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Chat Sessions Table */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mb-5">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-4 text-12-bold grey-700 uppercase">Chat Title</th>
                                    <th className="px-4 py-4 text-12-bold grey-700 uppercase">Date & Time</th>
                                    <th className="px-4 py-4 text-12-bold grey-700 uppercase">SOPs Referenced</th>
                                    <th className="px-4 py-4 text-12-bold grey-700 uppercase">Warning Flags</th>
                                    <th className="px-4 py-4 text-12-bold grey-700 uppercase"></th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {loading ? (
                                    <tr>
                                        <td colSpan={5} className="px-4 py-4 text-14-bold grey-800">Loading...</td>
                                    </tr>
                                ) : paginatedFilteredSessions.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="px-6 py-12 text-center !text-gray-500">No chats found</td>
                                    </tr>
                                ) : (
                                    paginatedFilteredSessions.map((session) => (
                                        <tr key={session.id} className="hover:bg-gray-50">
                                            <td className="px-4 py-4 text-14-bold grey-800 truncate max-w-[260px]" title={session.title}>{session.title}</td>
                                            <td className="px-4 py-4 text-14-reg grey-800 text-nowrap">
                                                <div className="flex items-center">
                                                    <img src="/icons/calendar.png" alt="Calendar" className="w-4 h-4 mr-2" />
                                                    {formatDate(session.date)}
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 text-14-reg grey-800 truncate max-w-[300px]" title={formatSopReferences(session.sopReferenced)}>{formatSopReferences(session.sopReferenced)}</td>
                                            <td className="px-4 py-4 text-14-reg grey-800">
                                                <div className="flex flex-col gap-1">
                                                    <span className={`flag-pill ${session.warnings > 0
                                                        ? 'warning-flag'
                                                        : 'safe-flag'
                                                        }`}>
                                                        {session.warnings > 0 ? 'Unsafe' : 'Safe'}
                                                    </span>
                                                    {session.warnings > 0 && (
                                                        <span className="text-xs text-red-600">
                                                            {session.warnings} warning{session.warnings > 1 ? 's' : ''}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-center">
                                                <button
                                                    className="p-2 rounded-lg hover:bg-gray-100"
                                                    onClick={async () => {
                                                        try {
                                                            // Load manager chat log messages (same as admin reports)
                                                            const response = await fetchManagerChatLogById(session.id);
                                                            const messages = response.messages || [];

                                                            setSelectedChat({
                                                                ...session,
                                                                messages: messages
                                                            });
                                                            setViewChatModalOpen(true);
                                                        } catch (error) {
                                                            // Handle chat log loading error
                                                        }
                                                    }}
                                                >
                                                    <Eye size={16} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>


                    {/* Pagination */}
                    <div className="global-pagination px-4 md:px-6 py-3 flex items-center justify-between border-t border-gray-200">
                        <div className="flex items-center gap-2 text-sm">
                            <button
                                className="btn-next-prev"
                                disabled={currentPage === 1}
                                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                            >
                                <i className="iconoir-nav-arrow-left"></i>
                            </button>
                            <div className="flex items-center gap-1">
                                {getPageNumbers(currentPage, Math.ceil(displayedTotal / rowsPerPage)).map((p: PageButton) => (
                                    <button
                                        key={p.key}
                                        disabled={p.disabled}
                                        onClick={() => typeof p.page === 'number' && handlePageChange(p.page)}
                                        className={`btn-page-number ${p.active ? 'color-renegade' : ''}`}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                            <button
                                className="btn-next-prev"
                                disabled={currentPage >= Math.ceil(displayedTotal / rowsPerPage)}
                                onClick={() => handlePageChange(Math.min(Math.ceil(displayedTotal / rowsPerPage), currentPage + 1))}
                            >
                                <i className="iconoir-nav-arrow-right"></i>
                            </button>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="flex items-center gap-2">
                                <input
                                    type="number"
                                    min="1"
                                    max="100"
                                    value={rowsPerPage}
                                    onChange={(e) => {
                                        const newPageSize = parseInt(e.target.value) || 9;
                                        if (newPageSize >= 1 && newPageSize <= 100) {
                                            setRowsPerPage(newPageSize);
                                            setCurrentPage(1); // Reset to first page when page size changes
                                        }
                                    }}
                                    className="page-size-input"
                                />
                                <span className="text-14-bold grey-400">Rows per page</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Chat View Modal */}
                {viewChatModalOpen && selectedChat && (
                    <Modal show={true} onHide={() => setViewChatModalOpen(false)} className="std-modal-width" centered backdrop="static">
                        <Modal.Header>
                            <Modal.Title className="text-gray-800">
                                {selectedChat.title} - {formatDate(selectedChat.date)}
                            </Modal.Title>
                            <i className="iconoir-xmark text-20-reg cursor-pointer" onClick={() => setViewChatModalOpen(false)}></i>
                        </Modal.Header>

                        <Modal.Body className="px-4 py-4">
                            {/* Chat Content */}
                            <div className="overflow-y-auto pr-2" style={{ maxHeight: '70vh' }}>
                                <div className="space-y-4">
                                    {selectedChat.messages && selectedChat.messages.length > 0 ? (
                                        selectedChat.messages.map((message, index) => {
                                            const assistantSourceLine =
                                                message.role === 'assistant'
                                                    ? resolveAssistantSourceLine(message)
                                                    : null;

                                            return (
                                            <div key={index} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                                <div>
                                                    {message.role === 'user' ? (
                                                        <div className="text-16-bold grey-800 bg-grey-50 rounded-[15px] px-4 py-3">
                                                            {message.text}
                                                        </div>
                                                    ) : (
                                                        <div className={`grey-700 admin-ai-chat-log ${message.is_flagged ? 'border-l-4 border-red-500 pl-3 py-2 bg-red-50' : ''}`}>
                                                            {message.is_flagged && (
                                                                <div className="flex items-center gap-2 mb-2 text-red-600 text-sm font-semibold">
                                                                    <span>⚠️</span>
                                                                    <span>Flagged: {message.detected_pattern || 'Safety warning detected'}</span>
                                                                </div>
                                                            )}
                                                            {formatAIText(message.text)}
                                                        </div>
                                                    )}
                                                    {assistantSourceLine && (
                                                        <div className="text-14-bold grey-800 max-w-[100%] mt-2">
                                                            <span>{assistantSourceLine}</span>
                                                        </div>
                                                    )}
                                                    <div
                                                        className={`text-13-reg text-capitalize grey-700 mt-2 ${message.role === 'user' ? 'text-right mt-2' : 'text-left mt-3'
                                                            }`}
                                                    >
                                                        {message.role === 'user' ? selectedChat.role : 'AI Assistant'} {' '}
                                                        [{message.timestamp
                                                            ? (() => {
                                                                const normalizedTimestamp = message.timestamp.endsWith('Z')
                                                                    ? message.timestamp
                                                                    : message.timestamp + 'Z';
                                                                const date = new Date(normalizedTimestamp);
                                                                return date.toLocaleTimeString('en-US', {
                                                                    hour: '2-digit',
                                                                    minute: '2-digit',
                                                                    hour12: true,
                                                                });
                                                            })()
                                                            : ''}]
                                                    </div>

                                                </div>
                                            </div>
                                            );
                                        })
                                    ) : (
                                        <div className="text-center text-gray-500 py-8">
                                            <p>No messages found in this chat session.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Modal.Body>
                    </Modal>
                )}
            </div>
        </>
    );
};

export default PastChats;
